import { getDefaultFieldValue, useFields } from '@modusign/react-signing';
import { FieldNode, SignatureField, SignatureType } from '@modusign/react-signing/types';
import { produce } from 'immer';
import { useSelector } from 'react-redux';

import { toRelativeSize } from '@app/features/apply-react-signing/components/FieldRenderer/libs/absRelConverter';
import { createAbsoluteSize } from '@app/features/apply-react-signing/components/FieldRenderer/libs/createAbsoluteSize';
import { Point, Viewport } from '@app/features/apply-react-signing/components/FieldRenderer/types';
import { isSignerResizableSignatureField } from '@app/features/apply-react-signing/libs/isSignerResizableSignatureField';
import { pageViewportSizesSelector, scaleSelector } from '@app/libs/reactPdfJs/store/selectors';

export function useSignatureFieldSize() {
  const viewports = useSelector(pageViewportSizesSelector) as Viewport[];
  const scale = useSelector(scaleSelector);

  const { changeFields } = useFields();

  function applyDefaultSize(draft: SignatureField, anchor: Point) {
    const defaultValue = getDefaultFieldValue({
      type: 'SIGNATURE',
      config: {
        allow: draft.config.allow,
      },
    });
    const viewport =
      viewports.find((v) => v.pageNumber === draft.position.page) ??
      createAbsoluteSize({ width: 0, height: 0 });

    draft.size = toRelativeSize(
      createAbsoluteSize({
        width: defaultValue.size.width * scale,
        height: defaultValue.size.height * scale,
      }),
      viewport
    );
    draft.position = {
      ...draft.position,
      x: draft.position.x - draft.size.width * anchor.x,
      y: draft.position.y - draft.size.height * anchor.y,
    };

    // 밀어 넣기
    draft.position.x += Math.max(0, -draft.position.x);
    draft.position.y += Math.max(0, -draft.position.y);
    draft.position.x += Math.min(0, 1.0 - (draft.position.x + draft.size.width));
    draft.position.y += Math.min(0, 1.0 - (draft.position.y + draft.size.height));
  }

  function changeAllow(allow: SignatureType[], signerId: string) {
    changeFields((fields) => {
      return fields.map(
        produce((draft: FieldNode) => {
          if (draft.participantReference.id === signerId && draft.type === 'SIGNATURE') {
            if (isSignerResizableSignatureField(draft)) {
              // 크기 조절이 가능한 서명/도장의 경우 기본 크기를 적용하고, position을 0.5 0.5 anchor로 그린 것으로 간주한다.
              applyDefaultSize(draft, { x: 0.5, y: 0.5 });
            }

            draft.config.allow = allow;
            applyDefaultSize(draft, { x: 0, y: 0 });
          }
        })
      ) as FieldNode[];
    });
  }

  function migrateSignatureFieldSize() {
    changeFields((fields) => {
      return fields.map(
        produce((draft: FieldNode) => {
          if (draft.type === 'SIGNATURE') {
            applyDefaultSize(draft, { x: 0.5, y: 0.5 });
          }
        })
      ) as FieldNode[];
    });
  }

  return {
    changeAllow,
    migrateSignatureFieldSize,
  };
}
